import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import authService from '../services/authService';
import { CheckCircle, AlertCircle, Loader, Calendar, User, Mail, Lock, MapPin, Hash } from 'lucide-react';
import './CompletarRegistoPage.css';

export default function CompletarRegistoPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const [userData, setUserData] = useState(null);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    cartaoCidadao: '',
    validoAte: '',
    nif: '',
    morada: '',
    codigoPostal: '',
    localidade: ''
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    if (!token) {
      setMessage('Token inválido');
      setLoading(false);
      return;
    }

    try {
      const result = await authService.validateRegistrationToken(token);
      if (result.valid) {
        setValidToken(true);
        setUserData(result.user);
      } else {
        setMessage(result.message || 'Link expirado ou inválido');
      }
    } catch (error) {
      setMessage('Erro ao validar link');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.password || formData.password.length < 6)
      newErrors.password = 'Password deve ter pelo menos 6 caracteres';

    if (formData.password !== formData.confirmPassword)
      newErrors.confirmPassword = 'Passwords não coincidem';

    if (!formData.cartaoCidadao || formData.cartaoCidadao.length < 8)
      newErrors.cartaoCidadao = 'Cartão de Cidadão inválido';

    if (!formData.validoAte)
      newErrors.validoAte = 'Data de validade obrigatória';

    if (!formData.nif || !/^\d{9}$/.test(formData.nif))
      newErrors.nif = 'NIF inválido (9 dígitos)';

    if (!formData.morada) newErrors.morada = 'Morada obrigatória';
    if (!formData.codigoPostal) newErrors.codigoPostal = 'Código postal obrigatório';
    if (!formData.localidade) newErrors.localidade = 'Localidade obrigatória';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      await authService.completeRegistration({
        token,
        password: formData.password,
        cartaoCidadao: formData.cartaoCidadao,
        validoAte: new Date(formData.validoAte).toISOString(),
        nif: formData.nif,
        morada: formData.morada,
        codigoPostal: formData.codigoPostal,
        localidade: formData.localidade
      });

      setMessage('Registo completo! A redirecionar para login...');
      setTimeout(() => navigate('/'), 2000);
    } catch (error) {
      setMessage(error.message || 'Erro ao completar registo');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="completar-registo-page">
        <div className="loading-container">
          <Loader className="spinner" size={48} />
          <p>A validar o seu link...</p>
        </div>
      </div>
    );
  }

  if (!validToken) {
    return (
      <div className="completar-registo-page">
        <div className="error-container">
          <div className="error-icon-wrapper">
            <AlertCircle size={64} />
          </div>
          <h2>Link Inválido ou Expirado</h2>
          <p>{message}</p>
          <button onClick={() => navigate('/')} className="btn-primary">
            Voltar à Página Inicial
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="completar-registo-page">
      <div className="registo-container">
        <div className="registo-header">
          <div className="header-icon">
            <Calendar size={48} />
          </div>
          <h1>Complete o Seu Registo</h1>
          <p className="header-subtitle">
            Olá <strong>{userData?.nome}</strong>, falta apenas preencher alguns dados para aceder à sua reserva
          </p>
          <div className="header-user-info">
            <div className="user-info-item">
              <Mail size={16} />
              <span>{userData?.email}</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="registo-form">
          {/* Dados de Acesso */}
          <div className="form-section">
            <div className="section-header">
              <Lock size={20} />
              <h3>Dados de Acesso</h3>
            </div>

            <div className="form-group">
              <label>
                <Lock size={16} />
                Password *
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Mínimo 6 caracteres"
                className={errors.password ? 'error' : ''}
              />
              {errors.password && <span className="error-msg">{errors.password}</span>}
            </div>

            <div className="form-group">
              <label>
                <Lock size={16} />
                Confirmar Password *
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Repita a password"
                className={errors.confirmPassword ? 'error' : ''}
              />
              {errors.confirmPassword && <span className="error-msg">{errors.confirmPassword}</span>}
            </div>
          </div>

          {/* Dados de Identificação */}
          <div className="form-section">
            <div className="section-header">
              <User size={20} />
              <h3>Dados de Identificação</h3>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  <Hash size={16} />
                  Cartão de Cidadão *
                </label>
                <input
                  type="text"
                  name="cartaoCidadao"
                  value={formData.cartaoCidadao}
                  onChange={handleChange}
                  placeholder="00000000 0 ZZ0"
                  className={errors.cartaoCidadao ? 'error' : ''}
                />
                {errors.cartaoCidadao && <span className="error-msg">{errors.cartaoCidadao}</span>}
              </div>

              <div className="form-group">
                <label>
                  <Calendar size={16} />
                  Válido Até *
                </label>
                <input
                  type="date"
                  name="validoAte"
                  value={formData.validoAte}
                  onChange={handleChange}
                  className={errors.validoAte ? 'error' : ''}
                />
                {errors.validoAte && <span className="error-msg">{errors.validoAte}</span>}
              </div>
            </div>

            <div className="form-group">
              <label>
                <Hash size={16} />
                NIF *
              </label>
              <input
                type="text"
                name="nif"
                value={formData.nif}
                onChange={handleChange}
                maxLength="9"
                placeholder="9 dígitos"
                className={errors.nif ? 'error' : ''}
              />
              {errors.nif && <span className="error-msg">{errors.nif}</span>}
            </div>
          </div>

          {/* Morada */}
          <div className="form-section">
            <div className="section-header">
              <MapPin size={20} />
              <h3>Morada</h3>
            </div>

            <div className="form-group">
              <label>
                <MapPin size={16} />
                Morada Completa *
              </label>
              <input
                type="text"
                name="morada"
                value={formData.morada}
                onChange={handleChange}
                placeholder="Rua, Número, Andar"
                className={errors.morada ? 'error' : ''}
              />
              {errors.morada && <span className="error-msg">{errors.morada}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Código Postal *</label>
                <input
                  type="text"
                  name="codigoPostal"
                  value={formData.codigoPostal}
                  onChange={handleChange}
                  placeholder="0000-000"
                  className={errors.codigoPostal ? 'error' : ''}
                />
                {errors.codigoPostal && <span className="error-msg">{errors.codigoPostal}</span>}
              </div>

              <div className="form-group">
                <label>Localidade *</label>
                <input
                  type="text"
                  name="localidade"
                  value={formData.localidade}
                  onChange={handleChange}
                  placeholder="Cidade"
                  className={errors.localidade ? 'error' : ''}
                />
                {errors.localidade && <span className="error-msg">{errors.localidade}</span>}
              </div>
            </div>
          </div>

          {message && (
            <div className={`alert-message ${message.includes('sucesso') || message.includes('completo') ? 'success' : 'error'}`}>
              <div className="alert-icon">
                {message.includes('sucesso') || message.includes('completo') ? (
                  <CheckCircle size={20} />
                ) : (
                  <AlertCircle size={20} />
                )}
              </div>
              <span>{message}</span>
            </div>
          )}

          <div className="form-actions">
            <button type="submit" disabled={submitting} className="btn-submit">
              {submitting ? (
                <>
                  <Loader className="spinner" size={20} />
                  A processar...
                </>
              ) : (
                <>
                  <CheckCircle size={20} />
                  Completar Registo
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}